import axios, { AxiosError } from 'axios';

const baseURL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('acs_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Tenant scope superadmin (installé module-level pour run dès le 1er query)
  const activeTenant = localStorage.getItem('acs_active_tenant');
  if (activeTenant) cfg.headers['X-Active-Tenant'] = activeTenant;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('acs_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Paginated<T> {
  current_page: number;
  data: T[];
  last_page: number;
  per_page: number;
  total: number;
}

export interface User {
  id: number;
  email: string;
  name: string | null;
  role: string;
  is_superadmin: boolean;
  tenant_id: number | null;
  tenant?: { id: number; slug: string; name: string };
}

export interface Device {
  id: number;
  tenant_id: number;
  customer_id: number | null;
  serial_number: string;
  mac_address: string | null;
  firmware_version: string | null;
  hardware_version: string | null;
  last_inform_at: string | null;
  is_online: boolean;
  uptime_seconds: number | null;
  bootstrap_state: string | null;
  provision_template_id: number | null;
  connection_request_url: string | null;
  adopted_at: string | null;
  provisioned_at: string | null;
  model?: { id: number; manufacturer: string; model_name: string };
  customer?: { id: number; name: string; external_id: string | null };
  tenant?: { id: number; slug: string; name: string };
  bandwidth_24h?: Array<{ h: number; in: number; out: number }>;
  notes?: string | null;
  metadata?: {
    wan_ip?: string;
    public_wan_ip?: string;
    is_nat?: boolean;
    [key: string]: unknown;
  };
}

export interface Customer {
  id: number;
  tenant_id: number;
  external_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  service_plan_id: number | null;
  status: string;
}

export interface InventoryEntry {
  id: number;
  tenant_id: number;
  serial_number: string;
  mac_address: string | null;
  external_id: string;
  customer_id: number | null;
  service_plan_id: number | null;
  notes: string | null;
  status: string;
  claimed_at: string | null;
  customer?: { id: number; name: string; external_id: string | null };
}

// ─── Mass Operations V2 ──────────────────────────────────────────────────────

export type MassOpKind = 'firmware_upgrade' | 'set_params' | 'reboot' | 'apply_template';
export type MassOpStatus = 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'aborted';
export type MassOpTargetStatus = 'pending' | 'queued' | 'running' | 'success' | 'failed' | 'skipped';

export interface MassOpFilter {
  tenant_id?: number;
  model_id?: number[];
  manufacturer?: string;
  firmware_lt?: string;
  firmware_gt?: string;
  mac_in?: string[];
  tag?: string;
  online?: boolean;
  bootstrap_state?: 'unknown' | 'adopted' | 'provisioned';
}

export interface MassOpProgress {
  total: number;
  pending: number;
  queued: number;
  running: number;
  success: number;
  failed: number;
  skipped: number;
  success_pct: number;
}

export interface MassOp {
  id: number;
  tenant_id: number;
  name: string;
  kind: MassOpKind;
  filter_json: MassOpFilter;
  payload_json: Record<string, unknown>;
  start_at: string;
  end_at: string;
  daily_from_h: number;
  daily_to_h: number;
  timezone: string;
  max_concurrent: number;
  exclude_weekends: boolean;
  status: MassOpStatus;
  behind_schedule: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  canary_count?: number | null;
  canary_wait_minutes?: number | null;
  canary_failure_threshold_pct?: number | null;
  canary_status?: 'in_progress' | 'passed' | 'failed' | null;
  canary_started_at?: string | null;
  canary_completed_at?: string | null;
  creator?: { id: number; email: string; name: string | null };
  progress?: MassOpProgress;
  concurrent?: { needed: number; current: number; cap: number; behind: boolean };
}

export interface MassOpTarget {
  id: number;
  mass_op_id: number;
  device_id: number;
  status: MassOpTargetStatus;
  attempts: number;
  last_error: string | null;
  next_attempt_at: string | null;
  queued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  is_canary: boolean;
  device?: { id: number; serial_number: string; mac_address: string | null; firmware_version: string | null };
}

export interface MassOpRun {
  id: number;
  target_id: number;
  attempt: number;
  action: string;
  result: 'success' | 'failed' | 'pending' | string;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface MassOpPreviewSample {
  id: number;
  serial_number: string;
  mac_address: string | null;
  firmware_version: string | null;
  model_id: number | null;
  is_online: boolean;
}

export interface MassOpPreviewResponse {
  count: number;
  sample: MassOpPreviewSample[];
}

export interface MassOpCreatePayload {
  name: string;
  kind: MassOpKind;
  tenant_id?: number;
  filter_json?: MassOpFilter;
  payload_json?: Record<string, unknown>;
  start_at: string;
  end_at: string;
  daily_from_h?: number;
  daily_to_h?: number;
  timezone?: string;
  max_concurrent?: number;
  exclude_weekends?: boolean;
  canary_count?: number | null;
  canary_wait_minutes?: number | null;
  canary_failure_threshold_pct?: number | null;
}

export interface ProvisionTemplate {
  id: number;
  name: string;
  tenant_id?: number | null;
}

export interface DeviceEvent {
  time: string;
  device_id: number;
  event_type: string;
  event_code: string | null;
  data: string | null;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const auth = {
  login: (email: string, password: string) =>
    api.post<{ access_token: string; user: User }>('/auth/login', { email, password }).then((r) => r.data),
  me: () => api.get<User>('/me').then((r) => r.data),
  logout: () => api.post('/auth/logout').then(() => localStorage.removeItem('acs_token')),
  changeMyPassword: (current_password: string, new_password: string) =>
    api.post<{ ok: boolean }>('/me/password', { current_password, new_password }).then((r) => r.data),
};

export interface ManagedUser {
  id: number;
  tenant_id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'viewer';
  is_active: boolean;
  is_superadmin: boolean;
  last_login_at?: string | null;
  created_at?: string;
  tenant?: { id: number; slug: string; name: string };
}

export interface FirmwareRelease {
  id: number;
  tenant_id: number | null;
  vendor: string;
  model: string;
  version: string;
  file_url: string;
  file_size: number | null;
  sha256: string | null;
  hw_versions: string[] | null;
  vendor_checksum: string | null;
  notes: string | null;
  is_active: boolean;
  uploaded_at: string;
  tenant?: { id: number; slug: string; name: string };
  uploader?: { id: number; email: string; name: string | null };
}

export interface FirmwareUpdate {
  id: number;
  device_id: number;
  firmware_id: number;
  triggered_by: number | null;
  status: 'queued' | 'downloading' | 'applied' | 'failed' | 'rolled_back';
  fault_code: string | null;
  fault_string: string | null;
  start_time: string | null;
  complete_time: string | null;
  triggered_at: string;
  completed_at: string | null;
  vendor?: string;
  model?: string;
  version?: string;
  triggered_by_email?: string | null;
}

export const firmware = {
  list: (params: { vendor?: string; model?: string } = {}) =>
    api.get<FirmwareRelease[]>('/firmware-releases', { params }).then((r) => r.data),
  upload: (form: FormData, onProgress?: (pct: number) => void) =>
    api.post<FirmwareRelease>('/firmware-releases', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,  // pas de timeout pour gros fichiers
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }).then((r) => r.data),
  promote: (id: number) =>
    api.patch<{ ok: boolean; firmware: FirmwareRelease }>(`/firmware-releases/${id}/promote`).then((r) => r.data),
  destroy: (id: number) => api.delete(`/firmware-releases/${id}`),
  pushTo: (deviceId: number, firmware_id: number) =>
    api.post<{ ok: boolean; firmware_update_id: number; fault: string | null }>(`/devices/${deviceId}/firmware-update`, { firmware_id }).then((r) => r.data),
  history: (deviceId: number) =>
    api.get<FirmwareUpdate[]>(`/devices/${deviceId}/firmware-updates`).then((r) => r.data),
};

export const users = {
  list: () => api.get<ManagedUser[]>('/users').then((r) => r.data),
  create: (body: { email: string; password: string; name?: string; role: 'admin' | 'viewer'; tenant_id?: number; is_active?: boolean; is_superadmin?: boolean }) =>
    api.post<ManagedUser>('/users', body).then((r) => r.data),
  update: (id: number, body: Partial<Pick<ManagedUser, 'email' | 'name' | 'role' | 'is_active' | 'is_superadmin'>>) =>
    api.patch<ManagedUser>(`/users/${id}`, body).then((r) => r.data),
  destroy: (id: number) => api.delete(`/users/${id}`),
  resetPassword: (id: number, password: string) =>
    api.post<{ ok: boolean }>(`/users/${id}/reset-password`, { password }).then((r) => r.data),
};

export const devices = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<Paginated<Device>>('/devices', { params }).then((r) => r.data),
  get: (id: number) => api.get<Device>(`/devices/${id}`).then((r) => r.data),
  update: (id: number, data: Partial<Device>) =>
    api.patch<Device>(`/devices/${id}`, data).then((r) => r.data),
  events: (id: number) => api.get<DeviceEvent[]>(`/devices/${id}/events`).then((r) => r.data),
  hosts: (id: number) => api.get<unknown[]>(`/devices/${id}/hosts`).then((r) => r.data),
  health: (id: number) =>
    api.get<{
      computed_at: string;
      data_window_hours: number;
      host_count_avg: number;
      overall_status: 'healthy' | 'info' | 'warning' | 'critical';
      alerts: Array<{ severity: 'info' | 'warning' | 'critical'; kind: string; subject: string; title: string; message: string }>;
    }>(`/devices/${id}/health`).then((r) => r.data),
  hostHistory: (id: number, mac: string, hours: number = 24) =>
    api.get<{
      metrics: Array<{ time: string; metric: string; value: number; labels: { mac: string } }>;
      bandwidth: Array<{ time: string; bytes_in: number; bytes_out: number; rate_in_bps: number | null; rate_out_bps: number | null }>;
    }>(`/devices/${id}/hosts/${encodeURIComponent(mac)}/history`, { params: { hours } }).then((r) => r.data),
  speedtests: (id: number) => api.get<unknown[]>(`/devices/${id}/speedtests`).then((r) => r.data),
  applyTemplate: (id: number, template_id?: number) =>
    api.post(`/devices/${id}/apply-template`, { template_id }).then((r) => r.data),
  previewTemplate: (id: number, template_id?: number) =>
    api.post<{
      template: { id: number; name: string } | null;
      changes: Array<{ kind: string; label: string; value: string }>;
      warnings: string[];
      error?: string;
    }>(`/devices/${id}/apply-template`, { template_id, dry_run: true }).then((r) => r.data),
  adopt: (id: number) =>
    api.post<{ ok: boolean; state: string; driver: string; changes: Record<string, [unknown, unknown]>; errors: string[] }>(`/devices/${id}/adopt`).then((r) => r.data),
  unadopt: (id: number) =>
    api.post<{ ok: boolean; state: string; driver: string }>(`/devices/${id}/unadopt`).then((r) => r.data),
  markProvisioned: (id: number, customer_id: number) =>
    api.post<{ ok: boolean; state: string; note: string }>(`/devices/${id}/mark-provisioned`, { customer_id, preserve_config: true }).then((r) => r.data),
  setParams: (id: number, params: Record<string, string | number | boolean>) =>
    api.post<{ ok: boolean; pushed: number }>(`/devices/${id}/set-params`, { params }).then((r) => r.data),
  setWifi: (id: number, items: Array<{ index: string; ssid?: string; password?: string }>) =>
    api.post<{ ok: boolean; driver: string; pushed: number; paths: string[]; applied: boolean; note: string }>(
      `/devices/${id}/wifi`, { items },
    ).then((r) => r.data),
  metrics: (id: number, hours: number = 24, metric?: string) =>
    api.get<Array<{ time: string; metric: string; value: number }>>(`/devices/${id}/metrics`, { params: { hours, metric } }).then((r) => r.data),
  bandwidth: (id: number, hours: number = 24) =>
    api.get<Array<{ time: string; bytes_in: number; bytes_out: number; rate_in_bps: number | null; rate_out_bps: number | null }>>(`/devices/${id}/bandwidth`, { params: { hours } }).then((r) => r.data),
  uptimeHistory: (id: number, days: number = 30) =>
    api.get<{
      device_id: number;
      period_days: number;
      uptime_pct: number | null;
      total_downtime_sec: number;
      event_count: number;
      is_online: boolean;
      last_inform_at: string | null;
      last_outage: { started_at: string; ended_at: string; duration_sec: number } | null;
      events: Array<{ event_type: 'online' | 'offline'; at: string; duration_sec: number | null; source: string }>;
    }>(`/devices/${id}/uptime-history`, { params: { days } }).then((r) => r.data),
  consumption: (id: number, from: string, to: string, iface?: string) =>
    api.get<{
      device_id: number;
      period_from: string;
      period_to: string;
      interfaces: Array<{ interface: string; rx_bytes: number; tx_bytes: number; samples: number; first_sample: string | null; last_sample: string | null }>;
      total: { rx_bytes: number; tx_bytes: number };
    }>(`/devices/${id}/consumption`, {
      // from/to sont des dates locales (YYYY-MM-DD) ; on les convertit en instants
      // ISO couvrant la journée LOCALE complète, sinon le serveur (UTC) interprète
      // « 2026-07-09 » comme la journée UTC, décalée de 4h vs Montréal.
      params: {
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59.999`).toISOString(),
        interface: iface,
      },
    }).then((r) => r.data),
  ports: (id: number) =>
    api.get<{ last_inform: string | null; ports: Port[]; network: NetworkInfo; driver?: string; capabilities?: Record<string, boolean> }>(`/devices/${id}/ports`).then((r) => r.data),
  refreshPorts: (id: number) =>
    api.post<{ queued: number }>(`/devices/${id}/refresh-ports`).then((r) => r.data),
  reboot: (id: number) =>
    api.post<{ ok: boolean }>(`/devices/${id}/reboot`).then((r) => r.data),
  speedtest: (id: number) =>
    api.post<{ ok: boolean; url: string; driver: string; speedtest_id: number }>(`/devices/${id}/speedtest`).then((r) => r.data),
  wanPing: (id: number, enable: boolean) =>
    api.post<{ ok: boolean; enable: boolean }>(`/devices/${id}/wan-ping`, { enable }).then((r) => r.data),
  openWebAccess: (id: number) =>
    api.post<{
      kind: 'direct' | 'proxy';
      url: string | null;
      username: string | null;
      password: string | null;
      proxy_url?: string | null;
      port_ready?: boolean;
      wait_note?: string | null;
      expires_at: string;
      duration_min: number;
    }>(`/devices/${id}/web-access/open`).then((r) => r.data),
  closeWebAccess: (id: number) =>
    api.post<{ ok: boolean }>(`/devices/${id}/web-access/close`).then((r) => r.data),
  proxyToken: (id: number) =>
    api.post<{ url: string; expires_at: string }>(`/devices/${id}/proxy-token`).then((r) => r.data),
  claim: (body: { serial_number?: string; mac_address?: string }) =>
    api.post<{ ok: boolean; device_id: number; serial_number: string; note: string }>('/devices/claim', body).then((r) => r.data),
};

export interface NetworkInfo {
  wan_ip: string | null;
  lan_ip: string | null;
  lan_mask: string | null;
  dhcp_start: string | null;
  dhcp_end: string | null;
  dhcp_enable: boolean | string | null;
  periodic_inform_sec?: number | null;
}

export interface Port {
  name: string;
  type: 'wan-eth' | 'lan-eth' | 'wifi';
  index: string;
  enable: boolean | string | null;
  status: string | null;
  max_bitrate?: number | string | null;
  duplex?: string | null;
  mac?: string | null;
  ssid?: string | null;
  channel?: number | string | null;
  standard?: string | null;
  tx_power?: number | null;
  passphrase?: string | null;
  security?: string | null;
  band?: string | null;      // SmartRG: OperatingFrequencyBand ("2.4GHz"/"5GHz")
  vap_type?: string | null;  // SmartRG: primary / guest / setup / mesh
}

export const customers = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<Paginated<Customer>>('/customers', { params }).then((r) => r.data),
  get: (id: number) => api.get<Customer>(`/customers/${id}`).then((r) => r.data),
  create: (data: Partial<Customer>) => api.post<Customer>('/customers', data).then((r) => r.data),
  update: (id: number, data: Partial<Customer>) =>
    api.patch<Customer>(`/customers/${id}`, data).then((r) => r.data),
  destroy: (id: number) => api.delete(`/customers/${id}`),
  devices: (id: number) => api.get<Device[]>(`/customers/${id}/devices`).then((r) => r.data),
};

export const inventory = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<Paginated<InventoryEntry>>('/inventory', { params }).then((r) => r.data),
  create: (data: Partial<InventoryEntry>) =>
    api.post<InventoryEntry>('/inventory', data).then((r) => r.data),
  bulkImport: (entries: Array<Partial<InventoryEntry>>) =>
    api.post<{ created: number; updated: number; linked: number }>('/inventory/bulk-import', { entries }).then((r) => r.data),
  destroy: (id: number) => api.delete(`/inventory/${id}`),
};

export const massOps = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<Paginated<MassOp>>('/mass-ops', { params }).then((r) => r.data),
  show: (id: number) => api.get<MassOp>(`/mass-ops/${id}`).then((r) => r.data),
  create: (body: MassOpCreatePayload) =>
    api.post<MassOp>('/mass-ops', body).then((r) => r.data),
  preview: (filter: MassOpFilter, tenantId?: number) =>
    api.post<MassOpPreviewResponse>('/mass-ops/preview', {
      filter_json: filter,
      ...(tenantId ? { tenant_id: tenantId } : {}),
    }).then((r) => r.data),
  arm: (id: number) => api.post<{ ok: boolean; target_count: number; status: string }>(`/mass-ops/${id}/arm`).then((r) => r.data),
  pause: (id: number) => api.post<MassOp>(`/mass-ops/${id}/pause`).then((r) => r.data),
  resume: (id: number) => api.post<MassOp>(`/mass-ops/${id}/resume`).then((r) => r.data),
  abort: (id: number) => api.post<MassOp>(`/mass-ops/${id}/abort`).then((r) => r.data),
  targets: (id: number, params: Record<string, unknown> = {}) =>
    api.get<Paginated<MassOpTarget>>(`/mass-ops/${id}/targets`, { params }).then((r) => r.data),
  runs: (id: number, params: Record<string, unknown> = {}) =>
    api.get<Paginated<MassOpRun>>(`/mass-ops/${id}/runs`, { params }).then((r) => r.data),
};

export const provisionTemplates = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<Paginated<ProvisionTemplate>>('/provision-templates', { params }).then((r) => r.data),
};

// ─── OLT / ONT module ─────────────────────────────────────────────────────────

export interface OltSummary {
  id: number;
  tenant_id?: number;
  name: string;
  vendor: string;
  model: string;
  driver: string;
  management_address: string;
  cli_port?: number;
  snmp_port?: number;
  snmp_version?: string | null;
  status: string;
  last_discovered_at: string | null;
  notes?: string | null;
  site_label?: string | null;
  has_cli_credentials?: boolean;
  has_snmp_credentials?: boolean;
  counts: { pons: number; onts: number; fxs: number };
}

export interface OltCreatePayload {
  name: string;
  vendor: string;
  model: string;
  driver: string;
  management_address: string;
  cli_port?: number;
  snmp_port?: number;
  snmp_version?: string;
  notes?: string | null;
  site_label?: string | null;
  cli_username?: string;
  cli_password?: string;
  snmp_community?: string;
}

export interface OntListItem {
  id: number;
  kind: 'ont';
  vendor_local_id: string;
  vendor_serial: string | null;
  label: string | null;
  model: string | null;
  firmware: string | null;
  admin_status: string;
  oper_status: string;
  link_pending: boolean;
  external_id: string | null;
  metadata: Record<string, unknown>;
  last_seen_at: string;
  olt?: { id: number; name: string; vendor: string; model: string; management_address: string };
  customer?: { id: number; name: string; external_id: string | null; status: string };
  data_service: 'active' | 'suspended' | null;
  voice_service: string | null;  // registration state: registered, unregistered, ...
  parent?: { id: number; kind: string; vendor_local_id: string; label: string | null };
}

export interface VoiceSub {
  id: number;
  phone_number: string;
  sip_auth_username: string;
  registration_state: string;
  last_state_at: string | null;
  voice_profile: { id: number; name: string } | null;
  metadata: Record<string, unknown> | null;
}

export interface FxsPort {
  id: number;
  vendor_local_id: string;
  label: string | null;
  admin_status: string;
  oper_status: string;
  voice_subscription: VoiceSub | null;
}

export interface OntDetailData extends Omit<OntListItem, 'olt' | 'customer' | 'parent'> {
  olt: { id: number; name: string; vendor: string; model: string; driver: string; management_address: string; last_discovered_at: string | null };
  pon: { id: number; kind: string; vendor_local_id: string; label: string | null; oper_status: string; metadata: Record<string, unknown> };
  customer: { id: number; name: string; external_id: string | null; phone: string | null; address_line1: string | null; city: string | null; status: string; created_at: string } | null;
  optical: {
    at: string;
    olt_rx_dbm: number | null;
    ont_rx_dbm: number | null;
    ont_tx_dbm: number | null;
    upstream_bip: number | null;
    downstream_bip: number | null;
    rdi: number | null;
    fiber_distance_m: number | null;
  } | null;
  uni_ports: Array<{
    id: number; vendor_local_id: string; label: string | null;
    admin_status: string; oper_status: string;
    config_status: string | null;
    max_mac: number | null;
    max_frame_size: number | null;
    connected_devices: Array<{ mac: string; ipv4: string | null; ipv6: string | null; type: string | null; seen_at: string; vendor: string | null }>;
    mac_scan_at: string | null;
  }>;
  cpe_devices: Array<{
    id: number;
    serial_number: string;
    mac_address: string | null;
    firmware: string | null;
    is_online: boolean;
    last_inform_at: string | null;
    manufacturer: string | null;
    model_name: string | null;
    wan_ip: string | null;
    public_wan_ip: string | null;
    is_nat: boolean;
    wan_ipv6: string | null;
    wan_ipv6_pd: string | null;
  }>;
  fxs_ports: FxsPort[];
  subscription: {
    id: number;
    status: string;
    activated_at: string | null;
    custom_up_kbps: number | null;
    custom_down_kbps: number | null;
    profiles: {
      bandwidth:       SubProfilePayload | null;
      wan_access:      SubProfilePayload | null;
      voice:           SubProfilePayload | null;
      voice_transport: SubProfilePayload | null;
    };
  } | null;
  active_alarms_count: number;
}

export interface SubProfilePayload {
  id: number;
  kind: string;
  name: string;
  is_parameterized: boolean;
  up_kbps: number | null;
  down_kbps: number | null;
  vlan: number | null;
  access_mode: string | null;
  ipv6_mode: string | null;
  vendor_refs: Record<string, unknown>;
}

export interface OpticalSample {
  at: string;
  olt_rx_dbm: number | null;
  ont_rx_dbm: number | null;
  ont_tx_dbm: number | null;
  upstream_bip: number | null;
  downstream_bip: number | null;
}

export interface OntEvent {
  id: number;
  kind: string;
  at: string;
  source: string | null;
  details: Record<string, unknown> | null;
}

export interface OntAlarm {
  id: number;
  severity: string;
  vendor_code: string | null;
  raised_at: string;
  cleared_at: string | null;
  acknowledged_at: string | null;
  code: string;
  name: string;
}

export interface ServiceProfileItem {
  id: number;
  kind: 'bandwidth' | 'voice' | 'wan_access' | 'voice_transport';
  name: string;
  is_parameterized: boolean;
  up_kbps: number | null;
  down_kbps: number | null;
  vlan: number | null;
  access_mode: string | null;
  mac_limit: number | null;
  vendor_refs: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  used_in_bundles?: number;
  used_by_voice_subs?: number;
}

export interface ServiceBundleItem {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  active_subscriptions?: number;
  profiles: Array<{
    id: number;
    kind: string;
    name: string;
    is_parameterized: boolean;
    up_kbps: number | null;
    down_kbps: number | null;
  }>;
}

export const olts = {
  list: () => api.get<{ data: OltSummary[] }>('/olts').then((r) => r.data.data),
  get:  (id: number) => api.get(`/olts/${id}`).then((r) => r.data.data),
  create: (data: OltCreatePayload) =>
    api.post<{ data: OltSummary }>('/olts', data).then((r) => r.data.data),
  update: (id: number, data: Partial<OltCreatePayload>) =>
    api.patch<{ data: OltSummary }>(`/olts/${id}`, data).then((r) => r.data.data),
  destroy: (id: number) => api.delete(`/olts/${id}`),
  discover: (id: number, skipOntDetail = true) =>
    api.post(`/olts/${id}/discover`, { skip_ont_detail: skipOntDetail }).then((r) => r.data),
  testConnection: (id: number) =>
    api.post<{ ok: boolean; summary?: unknown; error?: string }>(`/olts/${id}/test-connection`).then((r) => r.data),
};

export const onts = {
  list: (params: Record<string, unknown> = {}) =>
    api.get<{ data: OntListItem[]; meta: { total: number; current_page: number; per_page: number; last_page: number } }>('/onts', { params })
      .then((r) => r.data),
  get: (id: number) =>
    api.get<{ data: OntDetailData }>(`/onts/${id}`).then((r) => r.data.data),
  opticalHistory: (id: number, days = 7) =>
    api.get<{ data: OpticalSample[]; meta: { count: number; since: string } }>(`/onts/${id}/optical-history`, { params: { days } })
      .then((r) => r.data),
  events: (id: number, days = 14) =>
    api.get<{ data: { events: OntEvent[]; alarms: OntAlarm[] }; meta: { since: string } }>(`/onts/${id}/events`, { params: { days } })
      .then((r) => r.data),
  refresh: (id: number) =>
    api.post<{ data: OntDetailData }>(`/onts/${id}/refresh`).then((r) => r.data.data),
};

export const serviceProfiles = {
  list: (kind?: string) =>
    api.get<{ data: ServiceProfileItem[] }>('/service-profiles', { params: kind ? { kind } : {} })
      .then((r) => r.data.data),
  get: (id: number) =>
    api.get<{ data: ServiceProfileItem }>(`/service-profiles/${id}`).then((r) => r.data.data),
  create: (data: Partial<ServiceProfileItem>) =>
    api.post<{ data: ServiceProfileItem }>('/service-profiles', data).then((r) => r.data.data),
  update: (id: number, data: Partial<ServiceProfileItem>) =>
    api.patch<{ data: ServiceProfileItem }>(`/service-profiles/${id}`, data).then((r) => r.data.data),
  destroy: (id: number) =>
    api.delete(`/service-profiles/${id}`),
  bundles: () =>
    api.get<{ data: ServiceBundleItem[] }>('/service-bundles').then((r) => r.data.data),
};


// ─── Coax (SendTek CEM-738 EoC) ──────────────────────────────────────────────
export interface CoaxMasterSummary {
  id: number;
  tenant_id: number;
  site_id: number;
  name: string | null;
  model: string;
  nmu_mac: string;
  master_mac: string | null;
  management_address: string;
  nmu_firmware: string | null;
  status: string;
  last_seen_at: string | null;
  last_discovered_at: string | null;
  temperature_celsius: number | null;
  ep_total: number;
  ep_online: number;
}

export interface CoaxEpSummary {
  id: number;
  site: { id: number; name: string } | null;
  master: { id: number; name: string | null; management_address: string } | null;
  customer: { id: number; external_id: string | null; name: string | null } | null;
  mac: string;
  voip_mac: string | null;
  wifi_mac: string | null;
  model: string | null;
  note: string | null;
  mode: 'bridge' | 'routed';
  host_limit: number;
  coax_atten_db: number;
  wan_mode: 'static' | 'dhcp' | null;
  wan_static_ip: string | null;
  wan_vlan: number | null;
  voice_enabled: boolean;
  last_link_status: 'on' | 'off' | null;
  last_link_status_at: string | null;
  last_diag_at: string | null;
  last_up_rate_mbps: number | null;
  last_dn_rate_mbps: number | null;
  last_up_atten_db: number | null;
  last_dn_atten_db: number | null;
  provisioning_state: string;
  last_pushed_at: string | null;
}

export const coax = {
  masters: () => api.get<{ data: CoaxMasterSummary[] }>('/coax/masters').then((r) => r.data.data),
  master:  (id: number) => api.get(`/coax/masters/${id}`).then((r) => r.data.data),
  endpoints: () => api.get<{ data: CoaxEpSummary[] }>('/coax/endpoints').then((r) => r.data.data),
};
